<template>
  <RevDrawer
    :close-button-label="i18n(translations.cancel)"
    :name="modalName"
    :title="i18n(translations.shippingDetails)"
    @close="handleClose"
  >
    <template #body>
      <RevInfoBlock
        v-if="apiError"
        class="mt-16"
        :icon="IconWarning"
        :title="apiError.title"
        variant="danger"
      >
        {{ apiErrorMessage }}
      </RevInfoBlock>
      <RevForm :id="FORM_ID" :initial-values :validate @submit="handleSubmit">
        <template #default="{ values, errors }">
          <p class="my-16">
            {{ i18n(translations.shippingInfoText) }}
          </p>
          <RevInputSelect
            id="shipping-provider"
            v-model="values.carrierId"
            class="mb-16"
            :error="errors.carrierId"
            :label="i18n(translations.carriers)"
            :options="shippingProvidersOptions"
          />
          <RevInputText
            id="tracking-number"
            ref="tracking-number"
            v-model="values.trackingNumber"
            class="mb-16"
            :error="errors.trackingNumber"
            :label="i18n(translations.trackingNumber)"
          />

          <template v-for="orderLine in order.orderLines" :key="orderLine.id">
            <RevDivider class="my-16" />
            <div class="mb-8">
              <span class="body-1-bold block">
                {{ i18n(translations.orderLineItem) }}
              </span>
              <span>{{ orderLine.id }}</span>
            </div>
            <div class="mb-24">
              <span class="body-1-bold block">
                {{ i18n(translations.sku) }}
              </span>
              <span>{{ orderLine.sku }}</span>
            </div>

            <div v-if="shouldDisplayImeiField(orderLine)" class="mb-24">
              <div class="body-2-bold mb-8">
                {{
                  i18n(translations.enterIMEI, {
                    imei: order.orderLines.length > 1,
                  })
                }}
              </div>

              <RevInputText
                :id="'item-identifier-' + orderLine.id"
                v-model="values.itemIdentifiers[orderLine.id]"
                class="mb-8"
                :label="i18n(translations.imei)"
                :maxlength="IMEI_MAX_LENGTH"
              />

              <div class="caption">
                <RevLink :to="HOW_TO_FILL_IMEI_LINKS" underlined>
                  {{ i18n(translations.learnWhyIMEI) }}
                </RevLink>
              </div>
            </div>

            <div v-if="shouldDisplaySerialNumberField(orderLine)" class="mb-24">
              <div class="body-2-bold mb-8">
                {{
                  i18n(translations.enterSerialNumber, {
                    number: order.orderLines.length > 1,
                  })
                }}
              </div>

              <RevInputText
                :id="'serial-number-' + orderLine.id"
                v-model="values.itemIdentifiers[orderLine.id]"
                class="mb-8"
                :label="i18n(translations.serialNumber)"
              />
            </div>

            <template v-if="isConcernedByVatCollection">
              <div class="body-2-bold mb-8">
                {{ i18n(translations.selectYourVatRegime) }}
              </div>

              <RevRadio
                v-for="vatTypeOption in vatTypeOptions"
                :id="orderLine.id + '-' + vatTypeOption.value"
                :key="vatTypeOption.value"
                v-model="values.vatTypes[orderLine.id]"
                class="mb-2"
                name="vat-type-code"
                :value="vatTypeOption.value"
                variant="full"
              >
                <template #label>{{ vatTypeOption.label }}</template>
              </RevRadio>
            </template>
          </template>

          <RevInputFileUpload
            id="input-file"
            v-model="uploadedFiles"
            :accept="['.pdf']"
            :label-empty="i18n(translations.uploadReceipt)"
            :max-files="1"
          />
        </template>
      </RevForm>
    </template>
    <template #footer>
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-16"
          :form="FORM_ID"
          full-width="always"
          :loading="isLoading"
          type="submit"
          variant="primary"
        >
          {{
            i18n(translations.shipItem, {
              item: order.orderLines.length,
            })
          }}
        </RevButton>

        <RevLink @click="handleCancel">
          {{ i18n(translations.cancel) }}
        </RevLink>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { handleFileUpload } from '@backmarket/front-upload'
import { HttpApiError } from '@backmarket/http-api'
import { putMerchantOrdersShipOrder } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { getHttpBaseUrl } from '@backmarket/nuxt-module-http/getHttpBaseUrl'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDivider } from '@ds/components/Divider'
import { RevDrawer } from '@ds/components/Drawer'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { closeModal } from '@ds/components/ModalBase'
import { RevRadio } from '@ds/components/Radio'
import { IconWarning } from '@ds/icons/IconWarning'

import { getExternalLinkList } from '~/constants/externalLinks'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import type { Order, OrderLine } from '../../../../../../../../Tab.types'
import { useFiltersOptions } from '../../../../../../../../composables/useFiltersOptions'

import {
  FORM_ID,
  IMEI_MAX_LENGTH,
  MODAL_NAME,
} from './ShipOrderDrawer.constants'
import translations from './ShipOrderDrawer.translations'
import type { FormValues } from './ShipOrderDrawer.types'

const i18n = useI18n()

const props = defineProps<{ order: Order; refreshOrders: () => void }>()

const { openSuccessToast } = useTheToast()
const { isConcernedByVatCollection, defaultVat } = useSellerInfoStore()
const { shippingProvidersOptions, vatTypeOptions } = useFiltersOptions()
const { isFeatureEnabled } = useBusinessFeaturesStore()
const hasSupportFeatureEnabled = isFeatureEnabled('supportEnabled')

const { HOW_TO_FILL_IMEI_LINKS } = getExternalLinkList(i18n.currentLocale)

const apiError = ref<null | HttpApiError>(null)
const isLoading = ref(false)
const uploadedFiles = ref([])
const modalName = computed(() => MODAL_NAME + props.order.id)

const apiErrorMessage = computed(() => {
  const nestedError = apiError.value?.errors.find((subError) => subError.title)

  return nestedError?.detail ?? nestedError?.title
})

const initialValues: FormValues = {
  trackingNumber: props.order.shipping?.trackingNumber || '',
  carrierId: String(props.order.shipping?.carrier?.id) || '',
  itemIdentifiers: props.order.orderLines.reduce(
    (acc, curr) => ({ ...acc, [curr.id]: '' }),
    {},
  ),
  vatTypes: props.order.orderLines.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.id]: curr.vatType?.code || defaultVat?.code,
    }),
    {},
  ),
}
const validate = makeValidate<FormValues>({
  carrierId: required(i18n(translations.required)),
  trackingNumber: required(i18n(translations.required)),
})

function reset() {
  apiError.value = null
}

function shouldDisplayImeiField(orderLine: OrderLine) {
  return (
    orderLine.useImei && orderLine.quantity === 1 && hasSupportFeatureEnabled
  )
}
function shouldDisplaySerialNumberField(orderLine: OrderLine) {
  return (
    !orderLine.useImei &&
    orderLine.useItemIdentifier &&
    orderLine.quantity === 1
  )
}

async function uploadFileIfNeeded() {
  if (uploadedFiles.value.length >= 1) {
    return await handleFileUpload(
      `${getHttpBaseUrl()}/services/uploads`,
      'care_merchant_invoice',
      null,
      uploadedFiles.value[0],
    )
  }

  return null
}

async function handleSubmit(values: FormValues) {
  try {
    const uploadedFile = await uploadFileIfNeeded()

    await $httpFetch(putMerchantOrdersShipOrder, {
      pathParams: {
        orderId: props.order.id,
      },
      body: {
        carrierId: values.carrierId,
        trackingNumber: values.trackingNumber,
        orderLines: Object.keys(values.itemIdentifiers).map((id) => ({
          id,
          ...(values.vatTypes[id] && { vateType: values.vatTypes[id] }),
          ...(values.itemIdentifiers[id] && {
            itemIdentifier: values.itemIdentifiers[id],
          }),
        })),
        ...(uploadedFile?.status === 'success' && {
          invoiceDocumentHandle: uploadedFile.handle,
        }),
      },
    })

    openSuccessToast({
      content: i18n(translations.orderProcessed, {
        orderId: props.order.id,
      }),
    })
    props.refreshOrders()
    closeModal(modalName.value)
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isLoading.value = false
  }
}

function handleClose() {
  reset()
}

function handleCancel() {
  reset()
  closeModal(modalName.value)
}
</script>
